// Configuração da API
const API_BASE_URL = 'https://vega-solutions.site/api';

// Debug: verificar se a variável de ambiente está sendo lida
console.log('REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
console.log('API_BASE_URL:', API_BASE_URL);

// Função para obter dados do usuário logado
function getCurrentUser() {
    try {
        const userData = localStorage.getItem('currentUser');
        return userData ? JSON.parse(userData) : null;
    } catch (error) {
        console.error('Erro ao obter dados do usuário:', error);
        return null;
    }
}

// Função para fazer requisições à API
async function apiRequest(endpoint, options = {}) {
    const url = `${API_BASE_URL}/${endpoint}`;
    
    // Obter dados do usuário logado
    const currentUser = getCurrentUser();
    
    const defaultOptions = {
        headers: {
            'Content-Type': 'application/json',
        },
    };
    
    // Adicionar operadorId no header se disponível
    if (currentUser && currentUser.operadorId) {
        defaultOptions.headers['X-Empresa-ID'] = currentUser.operadorId;
    }
    
    const config = {
        ...defaultOptions,
        ...options,
        headers: {
            ...defaultOptions.headers,
            ...options.headers,
        },
    };
    
    try {
        const response = await fetch(url, config);

        const contentType = response.headers.get('content-type') || '';
        let data;
        let rawText = '';
        try {
            // tenta JSON em um clone (para não consumir o stream original)
            data = await response.clone().json();
        } catch (e) {
            // se falhar, lê como texto bruto do response original
            rawText = await response.text();
            try { data = JSON.parse(rawText); } catch { data = { error: rawText }; }
        }

        if (!response.ok) {
            throw new Error((data && data.error) || rawText || `HTTP ${response.status}`);
        }

        return data;
    } catch (error) {
        console.error('Erro na API:', error);
        throw error;
    }
}

// API para Itens
export const itemsAPI = {
    // Buscar itens com filtros
    getItems: (filters = {}) => {
        const currentUser = getCurrentUser();
        
        // Se não for administrador, filtrar apenas itens da operação do usuário
        if (currentUser && currentUser.cargo !== 'administrador' && currentUser.operadorId) {
            filters.operador_id = currentUser.operadorId;
        }
        
        const params = new URLSearchParams(filters);
        return apiRequest(`items.php?${params}`);
    },
    
    // Adicionar novo item
    addItem: (itemData) => {
        const currentUser = getCurrentUser();
        
        // Adicionar operadorId automaticamente se não for administrador
        if (currentUser && currentUser.cargo !== 'administrador' && currentUser.operadorId) {
            itemData.operador_id = currentUser.operadorId;
        }
        
        return apiRequest('items.php', {
            method: 'POST',
            body: JSON.stringify(itemData),
        });
    },
    
    // Adicionar itens em lote
    addItemsBulk: (itemsArray) => {
        if (!Array.isArray(itemsArray)) {
            throw new Error('addItemsBulk espera um array de itens');
        }
        // Garantir operador_id nos itens se necessário
        const currentUser = getCurrentUser();
        let payload = itemsArray;
        if (currentUser && currentUser.cargo !== 'administrador' && currentUser.operadorId) {
            payload = itemsArray.map(item => ({
                ...item,
                operador_id: item.operador_id || currentUser.operadorId,
            }));
        }
        return apiRequest('items.php', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    },
    
    // Atualizar item
    updateItem: (id, itemData) => {
        const currentUser = getCurrentUser();
        
        // Se não for administrador, adicionar operadorId para validação no backend
        if (currentUser && currentUser.cargo !== 'administrador' && currentUser.operadorId) {
            itemData.operador_id = currentUser.operadorId;
        }
        
        return apiRequest(`items.php?id=${id}`, {
            method: 'PUT',
            body: JSON.stringify(itemData),
        });
    },
    
    // Remover item
    deleteItem: (id) => {
        const currentUser = getCurrentUser();
        
        // Se não for administrador, adicionar operadorId para validação no backend
        if (currentUser && currentUser.cargo !== 'administrador' && currentUser.operadorId) {
            return apiRequest(`items.php?id=${id}&operador_id=${currentUser.operadorId}`, {
                method: 'DELETE',
            });
        }
        
        return apiRequest(`items.php?id=${id}`, {
            method: 'DELETE',
        });
    },
};

// API para Gestores (Lotes, Estoques, Bins)
export const gestoresAPI = {
    // Lotes
    getLotes: () => {
        const currentUser = getCurrentUser();
        const operadorId = currentUser?.operadorId;
        const url = operadorId ? `gestores.php?type=lotes&operador_id=${operadorId}` : 'gestores.php?type=lotes';
        return apiRequest(url);
    },
    addLote: (nome, operadorId) => {
        const currentUser = getCurrentUser();
        const finalOperadorId = operadorId || currentUser?.operadorId;
        return apiRequest('gestores.php?type=lotes', {
            method: 'POST',
            body: JSON.stringify({ nome, operador_id: finalOperadorId }),
        });
    },
    removeLote: (id) => apiRequest(`gestores.php?type=lotes&id=${id}`, {
        method: 'DELETE',
    }),
    
    // Estoques
    getEstoques: () => {
        const currentUser = getCurrentUser();
        const operadorId = currentUser?.operadorId;
        const url = operadorId ? `gestores.php?type=estoques&operador_id=${operadorId}` : 'gestores.php?type=estoques';
        return apiRequest(url);
    },
    addEstoque: (nome, operadorId) => {
        const currentUser = getCurrentUser();
        const finalOperadorId = operadorId || currentUser?.operadorId;
        return apiRequest('gestores.php?type=estoques', {
            method: 'POST',
            body: JSON.stringify({ nome, operador_id: finalOperadorId }),
        });
    },
    removeEstoque: (id) => apiRequest(`gestores.php?type=estoques&id=${id}`, {
        method: 'DELETE',
    }),
    
    // Bins
    getBins: () => {
        const currentUser = getCurrentUser();
        const operadorId = currentUser?.operadorId;
        const url = operadorId ? `gestores.php?type=bins&operador_id=${operadorId}` : 'gestores.php?type=bins';
        return apiRequest(url);
    },
    addBin: (nome, operadorId) => {
        const currentUser = getCurrentUser();
        const finalOperadorId = operadorId || currentUser?.operadorId;
        return apiRequest('gestores.php?type=bins', {
            method: 'POST',
            body: JSON.stringify({ nome, operador_id: finalOperadorId }),
        });
    },
    removeBin: (id) => apiRequest(`gestores.php?type=bins&id=${id}`, {
        method: 'DELETE',
    }),
    
    // Configurações
    getConfig: (chave) => apiRequest(`gestores.php?type=config&chave=${chave}`),
    setConfig: (chave, valor) => apiRequest('gestores.php?type=config', {
        method: 'POST',
        body: JSON.stringify({ chave, valor }),
    }),
};

// API para Uploads
export const uploadAPI = {
    // Upload XML
    uploadXML: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        // Obter dados do usuário logado
        const currentUser = getCurrentUser();
        const headers = {};
        
        // Adicionar empresaId no header se disponível
        if (currentUser && currentUser.empresaId) {
            headers['X-Empresa-ID'] = currentUser.empresaId;
        }
        
        return fetch(`${API_BASE_URL}/upload.php?type=xml`, {
            method: 'POST',
            headers: headers,
            body: formData,
        }).then(response => response.json());
    },
    
    // Upload CSV
    uploadCSV: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        // Obter dados do usuário logado
        const currentUser = getCurrentUser();
        const headers = {};
        
        // Adicionar empresaId no header se disponível
        if (currentUser && currentUser.empresaId) {
            headers['X-Empresa-ID'] = currentUser.empresaId;
        }
        
        return fetch(`${API_BASE_URL}/upload.php?type=csv`, {
            method: 'POST',
            headers: headers,
            body: formData,
        }).then(response => response.json());
    },
};

// Função para gerar próximo COD.SIS
export const getNextCodSis = async () => {
    const response = await gestoresAPI.getConfig('next_cod_sis');
    return response.data;
};

// Função para atualizar nome do usuário
export const updateUserName = async (userName) => {
    return gestoresAPI.setConfig('user_name', userName);
};

// Função para buscar nome do usuário
export const getUserName = async () => {
    const response = await gestoresAPI.getConfig('user_name');
    return response.data || 'Operador';
};

// API para Usuários (Gerenciamento)
export const usersAPI = {
    list: () => apiRequest('usuarios.php'),
    update: (payload) => apiRequest('usuarios.php', {
        method: 'PUT',
        body: JSON.stringify(payload),
    }),
    toggleActive: (id, ativo) => apiRequest('usuarios.php?action=toggle', {
        method: 'PUT',
        body: JSON.stringify({ id, ativo }),
    }),
    remove: (id) => apiRequest(`usuarios.php?id=${id}`, {
        method: 'DELETE',
    }),
};

export default {
    itemsAPI,
    gestoresAPI,
    uploadAPI,
    usersAPI,
    getNextCodSis,
    updateUserName,
    getUserName,
};